import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Landmark } from 'lucide-react';
import React from 'react';
import api from '../lib/api';

const POLL_INTERVAL = 12000; // 12 seconds
const SOUND_KEY = 'ptc_ach_sound_enabled';

// Tiny inline notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRl9vT19teleVFTVEZtdCAQAAAAABAAEARBYAAABEFgABAAgAZGF0YUlPVF8A';

function getSoundEnabled() {
  try { return localStorage.getItem(SOUND_KEY) !== 'false'; } catch { return true; }
}

function setSoundEnabled(val) {
  try { localStorage.setItem(SOUND_KEY, val ? 'true' : 'false'); } catch (e) { console.warn('[useAchNotifications] setSoundEnabled failed', e); }
}

function playNotificationSound() {
  if (!getSoundEnabled()) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch (e) { console.warn('[useAchNotifications] audio play failed', e); }
}

export { getSoundEnabled, setSoundEnabled };

export default function useAchNotifications(isInternal) {
  const seenIds = useRef(new Set());
  const intervalRef = useRef(null);

  const poll = useCallback(async () => {
    if (!isInternal) return;
    try {
      const res = await api.get('/ach-notifications');
      const notifications = res.data?.notifications || [];
      
      for (const notif of notifications) {
        if (seenIds.current.has(notif.id)) continue;
        seenIds.current.add(notif.id);

        // Show toast
        toast.success(
          React.createElement('div', { 'data-testid': 'ach-toast-content' },
            React.createElement('div', { className: 'font-semibold text-sm' }, 'Banking Information Received'),
            React.createElement('div', { className: 'text-xs text-slate-500 mt-0.5' },
              `${notif.account_holder || 'Unknown'} — ${notif.bank_name || ''}`
            ),
            React.createElement('div', { className: 'text-[10px] text-slate-400 mt-0.5' },
              `Deal: ${notif.client_name || ''} | ${notif.confirmation_number || ''}`
            )
          ),
          {
            duration: 8000,
            id: `ach-${notif.id}`,
            icon: React.createElement(Landmark, { className: 'h-4 w-4 text-emerald-600' }),
          }
        );

        playNotificationSound();

        // Mark as read
        api.patch(`/ach-notifications/${notif.id}/read`).catch((e) => console.warn('[useAchNotifications] mark-read failed', e));
      }
    } catch (e) { console.warn('[useAchNotifications] poll failed', e); }
  }, [isInternal]);

  useEffect(() => {
    if (!isInternal) return;
    poll(); // immediate first poll
    intervalRef.current = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [isInternal, poll]);
}
